const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=;]*)/g;

// Decode common HTML entities that break URL matching
const decodeHtmlEntities = (text) =>
  text.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"');

/**
 * Extract the first URL found in a text string.
 * Returns null if no URL is found.
 */
export const extractFirstUrl = (text) => {
  if (!text) return null;
  const decoded = decodeHtmlEntities(text);
  const matches = decoded.match(URL_REGEX);
  return matches ? matches[0] : null;
};

/**
 * Split text into segments of plain text and URLs.
 * Returns an array of { type: 'text'|'url', value: string }.
 */
export const splitTextAndUrls = (text) => {
  if (!text) return [];
  const decoded = decodeHtmlEntities(text);
  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(URL_REGEX.source, "g");
  let match;

  while ((match = regex.exec(decoded)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: decoded.slice(lastIndex, match.index) });
    }
    parts.push({ type: "url", value: match[0] });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < decoded.length) {
    parts.push({ type: "text", value: decoded.slice(lastIndex) });
  }

  return parts;
};

/**
 * Get the total length of only the non-URL text segments.
 */
export const getTextOnlyLength = (text) => {
  if (!text) return 0;
  const parts = splitTextAndUrls(text);
  return parts
    .filter((part) => part.type === "text")
    .reduce((sum, part) => sum + part.value.length, 0);
};

/**
 * Truncate text at a word boundary counting only non-URL characters toward the limit.
 * URLs are preserved intact so renderTextWithLinks can still recognize and hide them.
 */
export const trimTextPreservingUrls = (text, limit) => {
  if (!text) return "";
  const parts = splitTextAndUrls(text);
  let textCharCount = 0;
  let result = "";

  for (const part of parts) {
    if (part.type === "url") {
      result += part.value;
    } else {
      const remaining = limit - textCharCount;
      if (part.value.length <= remaining) {
        result += part.value;
        textCharCount += part.value.length;
      } else {
        const trimmed = part.value.substring(0, remaining);
        const lastSpace = trimmed.lastIndexOf(" ");
        result += lastSpace > 0 ? trimmed.substring(0, lastSpace) + " " : trimmed;
        break;
      }
    }
  }

  return result;
};
